<template>
    <div
        v-if="modelValue"
        class="organization-summary-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="organization-summary-modal-title"
        @click.self="close"
        @keydown.esc="close"
    >
        <div class="organization-summary-dialog">
            <header class="organization-summary-header">
                <div class="d-flex align-items-start gap-3">
                    <div class="organization-summary-header-icon">
                        <i class="material-icons">hub</i>
                    </div>
                    <div>
                        <p class="organization-summary-kicker mb-1">Admin command center</p>
                        <h2 id="organization-summary-modal-title" class="organization-summary-heading mb-1">
                            Organizations at a glance
                        </h2>
                        <p class="organization-summary-subheading mb-0">
                            A live view of people, schedules, and credits across the current organization and its locations.
                        </p>
                    </div>
                </div>
                <button type="button" class="organization-summary-close" aria-label="Close summary" @click="close">
                    <i class="material-icons">close</i>
                </button>
            </header>

            <div class="organization-summary-body">
                <div v-if="loading" class="summary-state summary-loading-state">
                    <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
                    <strong>Gathering your organization snapshot…</strong>
                    <span>Calculating schedules and billing health.</span>
                </div>

                <div v-else-if="error" class="summary-state summary-error-state">
                    <div class="summary-state-icon"><i class="material-icons">cloud_off</i></div>
                    <strong>We couldn’t load the summary.</strong>
                    <span>{{ error }}</span>
                    <button type="button" class="btn btn-sm btn-primary mt-2" @click="$emit('retry')">
                        <i class="material-icons text-sm me-1">refresh</i> Try again
                    </button>
                </div>

                <div v-else-if="organizations.length === 0" class="summary-state">
                    <div class="summary-state-icon"><i class="material-icons">domain_disabled</i></div>
                    <strong>No current organization found.</strong>
                    <span>Select an organization location to view its command center.</span>
                </div>

                <template v-else>
                    <div class="summary-overview-grid">
                        <div class="summary-overview-item overview-purple">
                            <span class="overview-icon"><i class="material-icons">domain</i></span>
                            <span><strong>{{ formatNumber(organizations.length) }}</strong><small>organizations</small></span>
                        </div>
                        <div class="summary-overview-item overview-teal">
                            <span class="overview-icon"><i class="material-icons">location_city</i></span>
                            <span><strong>{{ formatNumber(locationCount) }}</strong><small>locations</small></span>
                        </div>
                        <div class="summary-overview-item overview-amber">
                            <span class="overview-icon"><i class="material-icons">volunteer_activism</i></span>
                            <span><strong>{{ formatNumber(volunteerCount) }}</strong><small>active volunteers</small></span>
                        </div>
                        <div class="summary-overview-item overview-blue">
                            <span class="overview-icon"><i class="material-icons">event</i></span>
                            <span><strong>{{ formatNumber(instanceCount) }}</strong><small>instances · 30 days</small></span>
                        </div>
                    </div>

                    <article
                        v-for="(organization, index) in organizations"
                        :key="organization.organization_id"
                        class="organization-summary-section"
                    >
                        <div class="organization-summary-section-header" :class="`org-accent-${index % 4}`">
                            <div class="d-flex align-items-center gap-3 min-w-0">
                                <div class="organization-avatar">{{ initials(organization.organization_name) }}</div>
                                <div class="min-w-0">
                                    <div class="d-flex align-items-center flex-wrap gap-2">
                                        <h3 class="organization-summary-name mb-0">{{ organization.organization_name }}</h3>
                                        <span class="organization-status" :class="statusClass(organization.status)">
                                            {{ organization.status || 'unknown' }}
                                        </span>
                                    </div>
                                    <p class="organization-summary-meta mb-0">
                                        {{ organization.locations?.length || 0 }}
                                        {{ organization.locations?.length === 1 ? 'location' : 'locations' }}
                                        <span v-if="organization.description"> · {{ organization.description }}</span>
                                    </p>
                                </div>
                            </div>
                            <div class="organization-balance" :class="{ 'balance-warning': isLowBalance(organization) }">
                                <small>Available credits</small>
                                <strong>{{ formatCurrency(organization.balance) }}</strong>
                                <span v-if="organization.billing_threshold !== null && organization.billing_threshold !== undefined">
                                    Replenish below {{ formatCurrency(organization.billing_threshold) }}
                                </span>
                            </div>
                        </div>

                        <div class="organization-kpi-row">
                            <div><i class="material-icons text-purple">groups</i><span><strong>{{ formatNumber(organization.volunteer_count) }}</strong><small>volunteers</small></span></div>
                            <div><i class="material-icons text-indigo">admin_panel_settings</i><span><strong>{{ formatNumber(organization.admin_count) }}</strong><small>admins</small></span></div>
                            <div><i class="material-icons text-teal">event_available</i><span><strong>{{ formatNumber(organization.total_instances) }}</strong><small>total instances</small></span></div>
                            <div><i class="material-icons text-orange">person_check</i><span><strong>{{ formatNumber(organization.scheduled_persons_next_30_days) }}</strong><small>scheduled people</small></span></div>
                        </div>

                        <div v-if="organization.locations?.length" class="location-summary-grid">
                            <div
                                v-for="location in organization.locations"
                                :key="location.location_id"
                                class="location-summary-card"
                            >
                                <div class="location-summary-card-top">
                                    <div class="d-flex align-items-start gap-2 min-w-0">
                                        <span class="location-pin"><i class="material-icons">location_on</i></span>
                                        <div class="min-w-0">
                                            <h4 class="location-summary-name mb-1">{{ location.location_name }}</h4>
                                            <p class="location-summary-meta mb-0">
                                                {{ location.timezone || location.country || 'Timezone not set' }}
                                            </p>
                                        </div>
                                    </div>
                                    <span class="location-status" :class="statusClass(location.status)">
                                        {{ location.status || 'unknown' }}
                                    </span>
                                </div>

                                <div class="location-metrics">
                                    <div><i class="material-icons">volunteer_activism</i><span><strong>{{ formatNumber(location.volunteer_count) }}</strong><small>volunteers</small></span></div>
                                    <div><i class="material-icons">admin_panel_settings</i><span><strong>{{ formatNumber(location.admin_count) }}</strong><small>admins</small></span></div>
                                    <div><i class="material-icons">event</i><span><strong>{{ formatNumber(location.total_instances) }}</strong><small>total instances</small></span></div>
                                    <div><i class="material-icons">person</i><span><strong>{{ formatNumber(location.scheduled_persons_next_30_days) }}</strong><small>scheduled people</small></span></div>
                                </div>

                                <div class="location-summary-footer">
                                    <span>
                                        <i class="material-icons">account_balance_wallet</i>
                                        {{ formatCurrency(location.balance) }} credits
                                    </span>
                                    <span v-if="location.next_instance_at" title="Next activity instance">
                                        <i class="material-icons">schedule</i>
                                        {{ formatDate(location.next_instance_at) }}
                                    </span>
                                    <span v-else class="text-muted">No upcoming instances</span>
                                </div>
                                <div v-if="isLowBalance(location)" class="location-billing-warning">
                                    <i class="material-icons">warning</i>
                                    Credits are below the configured replenishment threshold.
                                </div>
                            </div>
                        </div>
                        <div v-else class="location-empty-state">
                            <i class="material-icons">location_off</i>
                            No locations are connected to this organization yet.
                        </div>
                    </article>
                </template>
            </div>

            <footer class="organization-summary-footer">
                <span><i class="material-icons">update</i> Metrics are calculated live for the next 30 days.</span>
                <button type="button" class="btn btn-sm btn-outline-secondary m-0" @click="close">Close summary</button>
            </footer>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    organizations: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['update:modelValue', 'retry']);

const close = () => emit('update:modelValue', false);

const locationCount = computed(() => props.organizations.reduce(
    (total, organization) => total + (organization.locations?.length || 0),
    0
));

const volunteerCount = computed(() => props.organizations.reduce(
    (total, organization) => total + Number(organization.volunteer_count || 0),
    0
));

const instanceCount = computed(() => props.organizations.reduce(
    (total, organization) => total + Number(organization.total_instances_next_30_days || 0),
    0
));

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0));

const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(amount);
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const initials = (value) => (value || 'Organization')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const statusClass = (status) => String(status || '').toLowerCase() === 'active'
    ? 'status-active'
    : 'status-inactive';

const isLowBalance = (entity) => entity.billing_threshold !== null
    && entity.billing_threshold !== undefined
    && Number(entity.balance || 0) <= Number(entity.billing_threshold);
</script>

<style scoped>
.organization-summary-overlay {
    position: fixed;
    z-index: 1100;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(19, 16, 42, 0.72);
    backdrop-filter: blur(7px);
}

.organization-summary-dialog {
    display: flex;
    width: min(1180px, 100%);
    max-height: min(92vh, 940px);
    flex-direction: column;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 24px;
    background: #f8fafc;
    box-shadow: 0 28px 80px rgba(15, 23, 42, 0.36);
}

.organization-summary-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.45rem 1.6rem;
    background:
        radial-gradient(circle at 92% 0%, rgba(255, 255, 255, 0.25), transparent 25%),
        linear-gradient(120deg, #312e81, #5b21b6 60%, #0f766e);
    color: #fff;
}

.organization-summary-header-icon {
    display: grid;
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.16);
}

.organization-summary-header-icon i {
    font-size: 26px;
}

.organization-summary-kicker {
    color: #ddd6fe;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.organization-summary-heading {
    color: #fff;
    font-size: 1.45rem;
    font-weight: 750;
}

.organization-summary-subheading {
    color: rgba(255, 255, 255, 0.76);
    font-size: 0.78rem;
}

.organization-summary-close {
    display: grid;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
}

.organization-summary-close:hover,
.organization-summary-close:focus-visible {
    background: rgba(255, 255, 255, 0.23);
}

.organization-summary-close i {
    font-size: 19px;
}

.organization-summary-body {
    overflow: auto;
    padding: 1.35rem 1.5rem;
}

.summary-overview-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.7rem;
    margin-bottom: 1.25rem;
}

.summary-overview-item {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    background: #fff;
}

.summary-overview-item strong,
.summary-overview-item small {
    display: block;
}

.summary-overview-item strong {
    color: #1e293b;
    font-size: 1.12rem;
    line-height: 1.1;
}

.summary-overview-item small {
    color: #64748b;
    font-size: 0.66rem;
}

.overview-icon {
    display: grid;
    width: 34px;
    height: 34px;
    place-items: center;
    border-radius: 10px;
}

.overview-icon i {
    font-size: 18px;
}

.overview-purple .overview-icon { background: #ede9fe; color: #6d28d9; }
.overview-teal .overview-icon { background: #ccfbf1; color: #0f766e; }
.overview-amber .overview-icon { background: #fef3c7; color: #b45309; }
.overview-blue .overview-icon { background: #dbeafe; color: #1d4ed8; }

.organization-summary-section {
    margin-bottom: 1.1rem;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    border-radius: 17px;
    background: #fff;
    box-shadow: 0 5px 15px rgba(15, 23, 42, 0.045);
}

.organization-summary-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.05rem;
    color: #fff;
}

.org-accent-0 { background: linear-gradient(120deg, #4338ca, #6d28d9); }
.org-accent-1 { background: linear-gradient(120deg, #0f766e, #0d9488); }
.org-accent-2 { background: linear-gradient(120deg, #b45309, #d97706); }
.org-accent-3 { background: linear-gradient(120deg, #1d4ed8, #2563eb); }

.organization-avatar {
    display: grid;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.32);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.17);
    font-size: 0.75rem;
    font-weight: 800;
}

.organization-summary-name {
    overflow: hidden;
    color: #fff;
    font-size: 1rem;
    font-weight: 750;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.organization-summary-meta {
    overflow: hidden;
    color: rgba(255, 255, 255, 0.73);
    font-size: 0.68rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.organization-status,
.location-status {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.45rem;
    border-radius: 999px;
    font-size: 0.6rem;
    font-weight: 800;
    text-transform: capitalize;
}

.status-active {
    background: rgba(220, 252, 231, 0.92);
    color: #166534;
}

.status-inactive {
    background: rgba(241, 245, 249, 0.92);
    color: #475569;
}

.organization-balance {
    display: flex;
    min-width: 150px;
    flex-direction: column;
    align-items: flex-end;
    padding: 0.45rem 0.65rem;
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 11px;
    background: rgba(0, 0, 0, 0.12);
}

.organization-balance small,
.organization-balance span {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.58rem;
}

.organization-balance strong {
    color: #fff;
    font-size: 1.05rem;
}

.organization-balance.balance-warning {
    background: rgba(127, 29, 29, 0.33);
}

.organization-kpi-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border-bottom: 1px solid #eef2f7;
}

.organization-kpi-row > div,
.location-metrics > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 0.85rem;
}

.organization-kpi-row > div + div {
    border-left: 1px solid #eef2f7;
}

.organization-kpi-row i,
.location-metrics i {
    font-size: 19px;
}

.organization-kpi-row strong,
.organization-kpi-row small,
.location-metrics strong,
.location-metrics small {
    display: block;
}

.organization-kpi-row strong,
.location-metrics strong {
    color: #1e293b;
    font-size: 0.9rem;
}

.organization-kpi-row small,
.location-metrics small {
    color: #64748b;
    font-size: 0.6rem;
}

.text-purple { color: #7c3aed; }
.text-indigo { color: #4f46e5; }
.text-teal { color: #0d9488; }
.text-orange { color: #ea580c; }

.location-summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 0.75rem;
    padding: 0.85rem;
    background: #f8fafc;
}

.location-summary-card {
    overflow: hidden;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    background: #fff;
}

.location-summary-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.75rem 0.75rem 0.6rem;
}

.location-pin {
    display: grid;
    width: 29px;
    height: 29px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 9px;
    background: #ecfeff;
    color: #0e7490;
}

.location-pin i {
    font-size: 17px;
}

.location-summary-name {
    overflow: hidden;
    color: #334155;
    font-size: 0.83rem;
    font-weight: 750;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.location-summary-meta {
    overflow: hidden;
    color: #94a3b8;
    font-size: 0.61rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.location-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f1f5f9;
}

.location-metrics > div:nth-child(odd) {
    border-right: 1px solid #f1f5f9;
}

.location-metrics > div:nth-child(-n + 2) {
    border-bottom: 1px solid #f1f5f9;
}

.location-metrics i {
    color: #64748b;
    font-size: 17px;
}

.location-summary-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.45rem;
    padding: 0.65rem 0.75rem;
    color: #64748b;
    font-size: 0.62rem;
}

.location-summary-footer span {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
}

.location-summary-footer i {
    color: #0f766e;
    font-size: 15px;
}

.location-billing-warning {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 0.75rem;
    background: #fff7ed;
    color: #9a3412;
    font-size: 0.61rem;
}

.location-billing-warning i {
    font-size: 15px;
}

.location-empty-state {
    padding: 1rem;
    color: #64748b;
    font-size: 0.7rem;
    text-align: center;
}

.location-empty-state i {
    margin-right: 0.25rem;
    vertical-align: middle;
    font-size: 16px;
}

.summary-state {
    display: flex;
    min-height: 280px;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    flex-direction: column;
    color: #64748b;
    font-size: 0.78rem;
    text-align: center;
}

.summary-state strong {
    color: #334155;
}

.summary-state-icon {
    display: grid;
    width: 48px;
    height: 48px;
    margin-bottom: 0.35rem;
    place-items: center;
    border-radius: 15px;
    background: #ede9fe;
    color: #6d28d9;
}

.summary-state-icon i {
    font-size: 25px;
}

.summary-loading-state .spinner-border {
    width: 2rem;
    height: 2rem;
    margin-bottom: 0.45rem;
}

.summary-error-state .summary-state-icon {
    background: #fee2e2;
    color: #b91c1c;
}

.organization-summary-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1.5rem;
    border-top: 1px solid #e2e8f0;
    background: #fff;
    color: #64748b;
    font-size: 0.66rem;
}

.organization-summary-footer > span {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
}

.organization-summary-footer i {
    font-size: 15px;
}

@media (max-width: 767px) {
    .organization-summary-overlay {
        padding: 0;
    }

    .organization-summary-dialog {
        width: 100%;
        max-height: 100vh;
        border-radius: 0;
    }

    .organization-summary-header,
    .organization-summary-body {
        padding-right: 1rem;
        padding-left: 1rem;
    }

    .summary-overview-grid,
    .organization-kpi-row {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .organization-kpi-row > div:nth-child(3) {
        border-left: 0;
        border-top: 1px solid #eef2f7;
    }

    .organization-kpi-row > div:nth-child(4) {
        border-top: 1px solid #eef2f7;
    }

    .organization-summary-section-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .organization-balance {
        width: 100%;
        align-items: flex-start;
    }

    .organization-summary-footer {
        align-items: flex-start;
        flex-direction: column;
        padding-right: 1rem;
        padding-left: 1rem;
    }
}
</style>
